import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react"
import PropTypes from "prop-types"
import { useSelector } from "react-redux"
import {
  ensurePusher,
  subscribeChannel,
  unsubscribeChannel,
} from "../services/pusher"
import {
  CHAT_MEMBERS_UPDATED_EVENT,
  DIRECT_CONTACT_UPDATED_EVENT,
  eventNameAliases,
  getChatMembersChannelName,
  getDirectContactsChannelName,
  parseDirectContactPayload,
} from "../constants/messagesRealtime"

const DIRECT_CONTACT_EVENTS = eventNameAliases(DIRECT_CONTACT_UPDATED_EVENT)
const CHAT_MEMBERS_EVENTS = eventNameAliases(CHAT_MEMBERS_UPDATED_EVENT)

/** Coalesce bursty invite/member activations into one sidebar refetch. */
const CHAT_MEMBERS_REFRESH_DEBOUNCE_MS = 350

const DirectContactsContext = createContext({
  subscribe: () => () => {},
  subscribeChatMembersUpdated: () => () => {},
})

/**
 * After login:
 * - user.direct-contacts.{userId} → direct.contact.updated (payload)
 * - user.chat-members.{userId} → chat.members.updated (empty signal → refetch my-members)
 */
const DirectContactsProvider = ({ children }) => {
  const isAuth = useSelector(state => state.auth.isAuth)
  const userId = useSelector(state => state.auth.currentUser?.id)
  const token = useSelector(state => state.auth.jid)
  const contactListenersRef = useRef(new Set())
  const chatMembersListenersRef = useRef(new Set())
  const chatMembersDebounceRef = useRef(null)

  const subscribe = useCallback(listener => {
    if (typeof listener !== "function") return () => {}
    contactListenersRef.current.add(listener)
    return () => {
      contactListenersRef.current.delete(listener)
    }
  }, [])

  const subscribeChatMembersUpdated = useCallback(listener => {
    if (typeof listener !== "function") return () => {}
    chatMembersListenersRef.current.add(listener)
    return () => {
      chatMembersListenersRef.current.delete(listener)
    }
  }, [])

  useEffect(() => {
    if (!isAuth || !userId || !token) return undefined

    const contactsChannelName = getDirectContactsChannelName(userId)
    const chatMembersChannelName = getChatMembersChannelName(userId)
    if (!contactsChannelName && !chatMembersChannelName) return undefined

    const pusher = ensurePusher({
      getAuthToken: () => token,
    })
    if (!pusher) return undefined

    const contactsChannel = contactsChannelName
      ? subscribeChannel(contactsChannelName)
      : null
    const chatMembersChannel = chatMembersChannelName
      ? subscribeChannel(chatMembersChannelName)
      : null

    const onContactUpdated = payload => {
      const contact = parseDirectContactPayload(payload)
      if (!contact) return
      contactListenersRef.current.forEach(listener => listener(contact))
    }

    const notifyChatMembersUpdated = () => {
      chatMembersListenersRef.current.forEach(listener => {
        try {
          listener()
        } catch {
          // Isolate listener failures so one subscriber cannot break others.
        }
      })
    }

    const onChatMembersUpdated = () => {
      // Empty signal — debounce so multiple activations in a burst share one refetch.
      if (chatMembersDebounceRef.current) {
        clearTimeout(chatMembersDebounceRef.current)
      }
      chatMembersDebounceRef.current = setTimeout(() => {
        chatMembersDebounceRef.current = null
        notifyChatMembersUpdated()
      }, CHAT_MEMBERS_REFRESH_DEBOUNCE_MS)
    }

    if (contactsChannel) {
      DIRECT_CONTACT_EVENTS.forEach(name =>
        contactsChannel.bind(name, onContactUpdated),
      )
    }
    if (chatMembersChannel) {
      CHAT_MEMBERS_EVENTS.forEach(name =>
        chatMembersChannel.bind(name, onChatMembersUpdated),
      )
    }

    return () => {
      if (chatMembersDebounceRef.current) {
        clearTimeout(chatMembersDebounceRef.current)
        chatMembersDebounceRef.current = null
      }
      if (contactsChannel) {
        DIRECT_CONTACT_EVENTS.forEach(name =>
          contactsChannel.unbind(name, onContactUpdated),
        )
        unsubscribeChannel(contactsChannelName)
      }
      if (chatMembersChannel) {
        CHAT_MEMBERS_EVENTS.forEach(name =>
          chatMembersChannel.unbind(name, onChatMembersUpdated),
        )
        unsubscribeChannel(chatMembersChannelName)
      }
    }
  }, [isAuth, userId, token])

  const value = useMemo(
    () => ({ subscribe, subscribeChatMembersUpdated }),
    [subscribe, subscribeChatMembersUpdated],
  )

  return (
    <DirectContactsContext.Provider value={value}>
      {children}
    </DirectContactsContext.Provider>
  )
}

DirectContactsProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useDirectContactUpdates = () => useContext(DirectContactsContext)

export default DirectContactsProvider
